import { useCallback } from 'react';
import { Table } from '@tanstack/react-table';
import { useTranslation } from '@sib/i18n';
import { Button, Badge, Loader } from '@sib/ui';
import { useDialog } from '@sib/ui/hooks';
import { X, Settings, Export, DataTableToolbarUpload } from '@sib/ui/icons';
import { DataTableSettingsDialog } from './DataTableSettingsDialog';

interface DataTableSettingsProps<T> {
  table: Table<T>;
  disabledColumns?: Set<string>;
  exportAction?: () => Promise<void> | void;
  isExportPending?: boolean;
  uploadAction?: () => void;
}

export const DataTableSettings = <T,>({
  table,
  disabledColumns,
  exportAction,
  isExportPending = false,
  uploadAction,
}: DataTableSettingsProps<T>) => {
  const { getState, setColumnFilters, setRowSelection } = table;
  const { t } = useTranslation();
  const settingsDialog = useDialog();

  const { rowSelection, columnFilters } = getState();

  const selectedRowsCount = Object.values(rowSelection).filter(Boolean).length;

  const appliedFilters = columnFilters.filter(
    f =>
      f.value !== undefined && f.value !== null && String(f.value).trim() !== ''
  );

  const handleExportClick = useCallback(() => {
    if (!exportAction || isExportPending) return;
    exportAction();
  }, [exportAction, isExportPending]);

  return (
    <>
      <div className="z-20 flex h-10 w-full shrink-0 items-stretch border-l border-l-transparent bg-white">
        <div className="bg-primary flex shrink-0 items-center gap-2 rounded-tl-sm px-4 pt-2 pb-2 sm:pb-0">
          <Button
            type="button"
            variant="outline"
            className="bg-primary h-7 border-white text-white hover:bg-white/10 hover:text-white"
            onClick={settingsDialog.handleOpen}
          >
            <Settings className="size-5" />
          </Button>
          {exportAction && (
            <Button
              type="button"
              variant="outline"
              className="bg-primary h-7 border-white text-white hover:bg-white/10 hover:text-white"
              onClick={handleExportClick}
              aria-label={t('export')}
            >
              {isExportPending ? (
                <Loader className="h-5 text-white" />
              ) : (
                <DataTableToolbarUpload className="pointer-events-none block text-white" />
              )}
            </Button>
          )}
          {uploadAction && (
            <Button
              type="button"
              variant="outline"
              className="bg-primary h-7 border-white text-white hover:bg-white/10 hover:text-white"
              onClick={uploadAction}
              aria-label={t('upload')}
            >
              <Export className="pointer-events-none block text-white" />
            </Button>
          )}
          {selectedRowsCount > 0 && (
            <Badge
              variant="secondary"
              className="hover:bg-secondary flex h-7 gap-2 rounded-md text-nowrap"
            >
              {t('selectedRows', {
                count: selectedRowsCount,
              })}
              <Button
                className="flex gap-1 text-xs"
                variant="ghost"
                size="custom"
                onClick={e => {
                  e.stopPropagation();
                  setRowSelection({});
                }}
              >
                <X className="block h-2 w-2 shrink-0 grow-0" />
                {t('clear')}
              </Button>
            </Badge>
          )}
          {appliedFilters.length > 0 && (
            <div className="flex w-full items-center justify-between gap-2">
              <span className="text-sm text-white">
                {t('appliedFilters', {
                  count: appliedFilters.length,
                })}
              </span>
              <Button
                className="text-white"
                variant="ghost"
                size="custom"
                onClick={e => {
                  e.stopPropagation();
                  setColumnFilters([]);
                }}
              >
                <X className="size-3" />
                {t('clearAllFilters')}
              </Button>
            </div>
          )}
        </div>
        <div className="relative h-full w-5 overflow-hidden">
          <div
            className="bg-primary absolute top-0 left-0 h-10 w-20"
            style={{ transformOrigin: 'top left', transform: 'rotate(65deg)' }}
          />
        </div>
      </div>
      <DataTableSettingsDialog
        onClose={settingsDialog.handleClose}
        table={table}
        open={settingsDialog.isOpen}
        disabledColumns={disabledColumns}
      />
    </>
  );
};
